/**
 * Capture list and card display helpers for pipeline progress.
 */
import {
  isCaptureMessagingComplete,
  isCaptureViewerPrepPendingForMessaging,
} from '../../utils/viewerReadiness';
import { VIEWER_PHASE_TITLES, resolveViewerPhaseCopy, VIEWER_LOADING_PHASE, VIEWER_LOADING_SURFACES } from '../../utils/viewerLoadingCopy';
import { readCaptureFrameFields } from '../../utils/pipeline/pipelineFrameFields';
import { resolveCapturePipelineState, PIPELINE_PHASE } from './state';
import { normalizeStatusTitle, isActivePipelineRunStatus, inferFailStageFromError, getFailedStageIndex, normalizeFailedStageIndexToUiSegment, stageIndexToLabel, getInterruptedServersOfflineTitle, resolveCaptureProcessingStatusForUi } from './stageMapping.js';
import {
  UPLOAD_INTERRUPTED_MESSAGE,
  resolveFailurePillLabel,
  resolveFailureDisplayMessage,
  getErrorMessage,
  shouldSuppressStaleInfraFailureUi,
} from './failures.js';

function pipelineHasAdvancedPastUpload(capture) {
  const { framesExtracted } = readCaptureFrameFields(capture?.processing || {});
  if (framesExtracted != null && Number(framesExtracted) > 0) return true;
  const stage = String(capture?.processing?.stage || '').toLowerCase();
  if (!stage || stage === 'upload' || stage === 'uploadpending' || stage === 'queued') return false;
  return true;
}

/** Age since the latest metadata touch — re-upload / re-queue refreshes updatedAt, not createdAt. */
function resolveProcessingStallAgeMs(capture) {
  const updated = capture?.updatedAt?.toMillis?.() ?? 0;
  const created = capture?.createdAt?.toMillis?.() ?? 0;
  const anchor = updated > 0 ? updated : created;
  return anchor ? Date.now() - anchor : Number.POSITIVE_INFINITY;
}

/**
 * Progress % for a capture row on the projects list (active upload context or Firestore progress).
 */
export function resolveCaptureListPercent(capture, {
  activeCaptureId,
  uploadPhase,
  uploadPercent,
  pipelinePercent,
  progressPercent,
}, { pollCachePercent = null } = {}) {
  if (!capture?.id) return null;
  if (capture.id === activeCaptureId) {
    if (uploadPhase === 'uploading') return uploadPercent;
    if (uploadPhase === 'queueing' || uploadPhase === 'pipeline' || uploadPhase === 'done') {
      return pipelinePercent;
    }
    return progressPercent;
  }
  const state = resolveCapturePipelineState({
    capture,
    poll: pollCachePercent != null ? { percent: pollCachePercent } : null,
  });
  if (state.uploadPercent != null) return state.uploadPercent;
  return null;
}

/**
 * Shared capture-list label inputs for center overlay vs stage badge.
 */
function resolveCaptureListLabelContext(capture, uploadCtx = {}, {
  pollCache = null,
  viewerRuntime = null,
  uploadError = null,
  backendOffline = false,
  rowStalled = false,
  activeStageTitle = null,
} = {}) {
  const isActiveReupload = capture.id === uploadCtx.activeCaptureId;
  const pipelineState = resolveCapturePipelineState({
    capture,
    poll: pollCache,
    uploadCtx: {
      ...uploadCtx,
      activeCaptureId: uploadCtx.activeCaptureId,
      uploadPhase: uploadCtx.uploadPhase,
      uploadError: isActiveReupload ? uploadError : (rowStalled ? UPLOAD_INTERRUPTED_MESSAGE : null),
    },
    viewerRuntime,
    backendOffline,
  });
  const stageTitle = pipelineState.status.label;
  const effectiveStatus = resolveCaptureProcessingStatusForUi(capture, { isActiveReupload });
  const pipelineRunActive = isActivePipelineRunStatus(capture?.processing?.status);
  const rowUploadError = isActiveReupload
    ? uploadError
    : (rowStalled ? UPLOAD_INTERRUPTED_MESSAGE : null);

  const uiState = deriveCaptureUiState({
    processingStatus: capture.processing?.status,
    failedAt: pipelineRunActive ? null : capture.processing?.failedAt,
    failureDetail: pipelineRunActive ? null : capture.processing?.failureDetail,
    uploadError: rowUploadError,
    backendOffline: backendOffline && (
      effectiveStatus === 'processing'
      || effectiveStatus === 'queued'
      || isActiveReupload
    ),
    stageTitle,
    persistedFailedStageIndex: pipelineRunActive ? -1 : capture.processing?.failedStageIndex,
    splatPath: capture.storage?.splatPath,
    splatCount: capture.processing?.splatCount,
    completedAt: capture.completedAt,
    viewerStatus: capture.storage?.viewerStatus,
    viewerPath: capture.storage?.viewerPath,
    progress: capture.progress,
    updatedAt: capture.updatedAt,
    isActiveReupload,
    proc: capture.processing || {},
  });

  const isFailedState = uiState.isFailedForUI || pipelineState.failure.isFailed;
  const isCancelled = pipelineState.phase === PIPELINE_PHASE.CANCELLED || uiState.uiState === 'cancelled';
  const isQueued = pipelineState.flags.isQueueing;
  const isUploadStage = pipelineState.phase === PIPELINE_PHASE.CLIENT_UPLOAD;
  const isFinalizing = pipelineState.phase === PIPELINE_PHASE.VIEWER_PREP;

  const failureReason = isFailedState && !isCancelled
    ? resolveFailurePillLabel({
      failureDetail: pipelineRunActive ? null : capture.processing?.failureDetail,
      progressMessage: capture?.progress?.message,
      polledFailedAt: pollCache?.failedAt ?? null,
      uploadError: rowUploadError,
      failedAt: pipelineRunActive ? null : capture.processing?.failedAt,
      failedStageIndex: uiState.failedStageIndex,
      stageTitle,
    })
    : '';

  return {
    pipelineState,
    uiState,
    failureReason,
    isFailedState,
    isCancelled,
    isQueued,
    isUploadStage,
    isFinalizing,
    stageTitle,
  };
}

/**
 * Center overlay on capture preview thumbnails — simplified while in-flight.
 * UPLOAD / QUEUEING / PROCESSING (post-upload); failures keep short reason text.
 */
export function resolveCaptureListCenterTitle(capture, uploadCtx = {}, options = {}) {
  if (!capture || typeof capture !== 'object') return 'PROCESSING';
  const { pipelineState, failureReason, isCancelled } = resolveCaptureListLabelContext(capture, uploadCtx, options);
  if (isCancelled) return 'CANCELLED';
  if (failureReason) return failureReason;
  return pipelineState.status.label;
}

/**
 * Stage badge under capture cards — matches ReconstructionPipeline friendly labels.
 */
export function resolveCaptureListPillTitle(capture, uploadCtx = {}, options = {}) {
  return resolveCaptureListCenterTitle(capture, uploadCtx, options);
}

/**
 * Processing rows must always show a numeric %; missing % after a grace window means stalled.
 * Returns { percent, stalled } — percent may be 0 during the grace window when backend has not written yet.
 */
export function resolveCaptureListDisplayPercent(capture, uploadCtx, {
  graceMs = 2 * 60 * 1000,
  pollCachePercent = null,
} = {}) {
  const raw = resolveCaptureListPercent(capture, uploadCtx, { pollCachePercent });
  if (raw != null && Number.isFinite(raw)) {
    return { percent: raw, stalled: false };
  }
  const status = capture?.processing?.status || 'processing';
  if (status !== 'processing') return { percent: null, stalled: false };
  if (pipelineHasAdvancedPastUpload(capture)) {
    return { percent: 0, stalled: false };
  }
  const ageMs = resolveProcessingStallAgeMs(capture);
  if (ageMs < graceMs) return { percent: 0, stalled: false };
  return { percent: null, stalled: true };
}

/**
 * Shared UI state contract for capture cards and LoadingUI title selection.
 */
export function deriveCaptureUiState({
  processingStatus,
  failedAt,
  failureDetail = null,
  uploadError,
  backendOffline,
  stageTitle,
  persistedFailedStageIndex,
  splatPath,
  splatCount,
  completedAt,
  viewerStatus,
  viewerPath,
  progress,
  updatedAt,
  isActiveReupload = false,
  proc = {},
}) {
  const metadataForUi = {
    processing: { status: processingStatus, splatCount },
    storage: { splatPath, viewerStatus, viewerPath },
    completedAt,
    progress,
    updatedAt,
  };
  const status = resolveCaptureProcessingStatusForUi(metadataForUi, { isActiveReupload });
  const messagingComplete = isCaptureMessagingComplete(metadataForUi, { isActiveReupload });
  const isDelivered = status === 'completed';
  const isCancelled = String(processingStatus || '').toLowerCase() === 'cancelled';
  const failStage = failedAt ?? null;
  const hasUploadError = typeof uploadError === 'string' && uploadError.trim().length > 0;
  const hasFailedStage = !!failStage && !isActivePipelineRunStatus(status);
  const offlineByError = hasUploadError && /offline|servers|fetch|network/i.test(uploadError);
  const isInterrupted = !!backendOffline || offlineByError;
  const isFailed = !isDelivered && (status === 'failed' || hasUploadError || hasFailedStage);
  const resolvedStageTitle = normalizeStatusTitle(stageTitle);
  const derivedFailedIdx = getFailedStageIndex(
    failStage || (isInterrupted ? 'Upload' : (hasUploadError ? inferFailStageFromError(uploadError) : null))
  );
  const failedStageIndex = persistedFailedStageIndex != null && persistedFailedStageIndex >= 0
    ? normalizeFailedStageIndexToUiSegment(persistedFailedStageIndex, failStage)
    : derivedFailedIdx;

  if (messagingComplete) {
    return {
      uiState: 'completed',
      titleText: 'RECONSTRUCTION COMPLETE',
      messageText: '',
      failedStageIndex: -1,
      isFailedForUI: false,
      interrupted: false,
    };
  }

  // Cancel clears failedAt, so without this branch a cancelled capture falls through
  // to the generic tail and renders as "PROCESSING" under failed styling.
  if (isCancelled && !isDelivered) {
    return {
      uiState: 'cancelled',
      titleText: 'CANCELLED',
      messageText: 'This capture was cancelled before it finished processing.',
      failedStageIndex: -1,
      isFailedForUI: true,
      interrupted: false,
    };
  }

  if (
    isCaptureViewerPrepPendingForMessaging(metadataForUi, { isActiveReupload })
    || (String(processingStatus || '').toLowerCase() === 'completed' && !messagingComplete)
  ) {
    const viewerPrepCopy = resolveViewerPhaseCopy({
      phase: VIEWER_LOADING_PHASE.VIEWER_PREP,
      surface: VIEWER_LOADING_SURFACES.CAPTURE,
    });
    return {
      uiState: 'processing',
      titleText: viewerPrepCopy?.title || VIEWER_PHASE_TITLES.VIEWER_PREP,
      messageText: viewerPrepCopy?.detail || 'Optimizing your capture for the web viewer. Large scenes can take a few minutes.',
      failedStageIndex: -1,
      isFailedForUI: false,
      interrupted: false,
    };
  }

  if (status === 'queued') {
    return {
      uiState: 'queued',
      titleText: 'QUEUEING',
      messageText: "Waiting for GPU to start. Don't worry, this will only take a moment.",
      failedStageIndex: -1,
      isFailedForUI: false,
      interrupted: false,
    };
  }

  if (isInterrupted && status !== 'completed') {
    return {
      uiState: 'interrupted',
      titleText: getInterruptedServersOfflineTitle(),
      messageText: getErrorMessage('Upload', uploadError, true, failedStageIndex),
      failedStageIndex: failedStageIndex >= 0 ? failedStageIndex : 0,
      isFailedForUI: true,
      interrupted: true,
    };
  }

  if (
    shouldSuppressStaleInfraFailureUi({
      processing: {
        ...proc,
        status: processingStatus,
        failedAt: failStage,
        failureDetail,
      },
      progress,
    })
  ) {
    return {
      uiState: 'processing',
      titleText: resolvedStageTitle || 'PROCESSING',
      messageText: 'Processing reconstruction…',
      failedStageIndex: -1,
      isFailedForUI: false,
      interrupted: false,
    };
  }

  if (isFailed) {
    const stageLabel = failedStageIndex >= 0 ? stageIndexToLabel(failedStageIndex) : null;
    const resolvedFailureDetail =
      (typeof failureDetail === 'string' && failureDetail.trim())
        ? failureDetail.trim()
        : (uploadError || null);
    return {
      uiState: 'failed',
      titleText: stageLabel ? `FAILED AT ${stageLabel}` : 'FAILED',
      messageText: resolveFailureDisplayMessage({
        failureDetail: resolvedFailureDetail,
        failedAt: failStage,
        failedStageIndex,
      }),
      failedStageIndex,
      isFailedForUI: true,
      interrupted: false,
    };
  }

  return {
    uiState: 'processing',
    titleText: resolvedStageTitle || 'PROCESSING',
    messageText: 'Processing reconstruction…',
    failedStageIndex: -1,
    isFailedForUI: false,
    interrupted: false,
  };
}